// client/src/utils/orgDataUtils.js
// Organization-scoped data fetching utilities

import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  query, 
  where,
  doc,
  getDoc 
} from 'firebase/firestore';
import { apiFetch } from '../services/apiClient';
import { 
  isSuperAdmin,
  isAdmin,
  filterUsersByOrgAccess,
  canViewOrganization,
  canViewOrganizationBasicInfo 
} from './roleUtils';

/**
 * Fetch users within specific organizations (with permission checking)
 * @param {Array<string>} organizationIds - Organization IDs to fetch users from
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @returns {Promise<Array<Object>>} - Filtered users array
 */
export const fetchUsersInOrganizations = async (organizationIds, currentUserRole, currentUserOrgIds = []) => {
  try {
    if (!Array.isArray(organizationIds) || organizationIds.length === 0) {
      return [];
    }
    
    // Superadmins can fetch users from any organization
    const accessibleOrgIds = isSuperAdmin(currentUserRole) 
      ? organizationIds 
      : organizationIds.filter(orgId => currentUserOrgIds.includes(orgId));
    
    if (accessibleOrgIds.length === 0) {
      return [];
    }
    
    // Fetch users who belong to any of the accessible organizations
    const usersSnapshot = await getDocs(
      query(
        collection(db, 'users'),
        where('organizationIds', 'array-contains-any', accessibleOrgIds)
      )
    );
    
    const users = [];
    usersSnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Apply additional filtering based on role permissions
    return filterUsersByOrgAccess(users, currentUserRole, currentUserOrgIds);
  } catch (error) {
    console.error('Error fetching users in organizations:', error);
    return [];
  }
};

/**
 * Fetch all users current user can manage
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @returns {Promise<Array<Object>>} - Users the current user can manage
 */
export const fetchManageableUsers = async (currentUserRole, currentUserOrgIds = []) => {
  try {
    let usersSnapshot;
    
    if (isSuperAdmin(currentUserRole)) {
      // Superadmins can manage all users
      usersSnapshot = await getDocs(collection(db, 'users'));
    } else {
      // Regular admins can only manage users in their organizations
      if (currentUserOrgIds.length === 0) {
        return [];
      }
      
      usersSnapshot = await getDocs(
        query(
          collection(db, 'users'),
          where('organizationIds', 'array-contains-any', currentUserOrgIds)
        )
      );
    }
    
    const users = [];
    usersSnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return filterUsersByOrgAccess(users, currentUserRole, currentUserOrgIds);
  } catch (error) {
    console.error('Error fetching manageable users:', error);
    return [];
  }
};

/**
 * Fetch organizations that user has ADMIN access to view (full details)
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @returns {Promise<Array<Object>>} - Organizations user can view (admin access)
 */
export const fetchAccessibleOrganizations = async (currentUserRole, currentUserOrgIds = []) => {
  try {
    if (!isAdmin(currentUserRole)) {
      return [];
    }

    const response = await apiFetch('/api/admin/organizations', { authenticated: true });
    const organizations = response.organizations || [];

    return organizations.filter((org) =>
      canViewOrganization(currentUserRole, currentUserOrgIds, org.id)
    );
  } catch (error) {
    console.error('Error fetching accessible organizations:', error);

    // Fallback: fetch org docs directly for the caller's assigned organizations
    if (!Array.isArray(currentUserOrgIds) || currentUserOrgIds.length === 0) {
      return [];
    }

    try {
      const organizations = await Promise.all(
        currentUserOrgIds.map(async (orgId) => {
          const orgDoc = await getDoc(doc(db, 'organizations', orgId));
          if (!orgDoc.exists()) return null;
          const orgData = orgDoc.data();
          if (!orgData.isActive) return null;
          return { id: orgDoc.id, ...orgData };
        })
      );

      return organizations
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (fallbackError) {
      console.error('Fallback organization fetch failed:', fallbackError);
      return [];
    }
  }
};

/**
 * Fetch basic organization info (name only) for organizations user belongs to
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @returns {Promise<Array<Object>>} - Organizations with basic info only
 */
export const fetchUserOrganizationNames = async (currentUserRole, currentUserOrgIds = []) => {
  try {
    if (currentUserOrgIds.length === 0) {
      return [];
    }

    if (isAdmin(currentUserRole)) {
      const organizations = await fetchAccessibleOrganizations(currentUserRole, currentUserOrgIds);
      return organizations.map((org) => ({ id: org.id, name: org.name }));
    }

    const organizations = await Promise.all(
      currentUserOrgIds.map(async (orgId) => {
        if (!canViewOrganizationBasicInfo(currentUserRole, currentUserOrgIds, orgId)) {
          return null;
        }
        const orgDoc = await getDoc(doc(db, 'organizations', orgId));
        if (!orgDoc.exists()) return null;
        const orgData = orgDoc.data();
        if (!orgData.isActive) return null;
        return { id: orgId, name: orgData.name };
      })
    );

    return organizations
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Error fetching user organization names:', error);
    return [];
  }
};

/**
 * Get organization statistics for admin dashboard
 * @param {string} organizationId - Organization ID to get stats for
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @returns {Promise<Object|null>} - Organization statistics or null if no access
 */
export const getOrganizationStats = async (organizationId, currentUserRole, currentUserOrgIds = []) => {
  try {
    // Check if user can view this organization
    if (!canViewOrganization(currentUserRole, currentUserOrgIds, organizationId)) {
      return null;
    }
    
    // Fetch organization details
    const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
    if (!orgDoc.exists()) {
      return null;
    }
    
    // Fetch users in this organization
    const usersSnapshot = await getDocs(
      query(
        collection(db, 'users'),
        where('organizationIds', 'array-contains', organizationId)
      )
    );
    
    const users = [];
    usersSnapshot.forEach((doc) => {
      users.push(doc.data());
    });
    
    // Calculate statistics
    const totalUsers = users.length;
    const studentCount = users.filter(user => user.role === 'student').length;
    const adminCount = users.filter(user => user.role === 'admin').length;
    const superadminCount = users.filter(user => user.role === 'superadmin').length;
    
    // Get recent activity (users active in last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const activeUsers = users.filter(user => {
      if (!user.last_active_time) return false;
      const lastActive = user.last_active_time.toDate ? user.last_active_time.toDate() : new Date(user.last_active_time);
      return lastActive > thirtyDaysAgo;
    }).length;
    
    return {
      organization: {
        id: orgDoc.id,
        ...orgDoc.data()
      },
      stats: {
        totalUsers,
        studentCount,
        adminCount,
        superadminCount,
        activeUsers,
        lastUpdated: new Date()
      }
    };
  } catch (error) {
    console.error('Error fetching organization stats:', error);
    return null;
  }
};

/**
 * Validate if user can perform action on target user
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @param {Object} targetUser - Target user object
 * @param {string} action - Action to perform ('view', 'edit', 'delete')
 * @returns {boolean} - Whether action is allowed
 */
export const validateUserAction = (currentUserRole, currentUserOrgIds = [], targetUser, action) => {
  if (!targetUser) return false;
  
  const targetUserOrgIds = targetUser.organizationIds || [];
  
  // Check if users share any organizations
  const sharedOrgs = currentUserOrgIds.filter(orgId => targetUserOrgIds.includes(orgId));
  
  switch (action) {
    case 'view':
      return isSuperAdmin(currentUserRole) || sharedOrgs.length > 0;
      
    case 'edit':
    case 'delete':
      // Cannot modify superadmins (unless you are one)
      if (targetUser.role === 'superadmin' && !isSuperAdmin(currentUserRole)) {
        return false;
      }
      
      // Must be admin or superadmin with shared organization
      return (currentUserRole === 'admin' || isSuperAdmin(currentUserRole)) && 
             (isSuperAdmin(currentUserRole) || sharedOrgs.length > 0);
      
    default:
      return false;
  }
};
